import { DateUtils } from '_lib/utils/date.utils';

import {
  createApiResource,
  CreateResource,
  UnpagedListResource,
  DeleteResource,
  UnpagedListOptions,
  CustomQueryHookResult,
  QueryKey,
  useCustomQuery
} from '../apiResourceFactory';
import { API } from '../apiService';
import {
  AppointmentDetailDTO,
  AppointmentAvailabilityDTO,
  AppointmentCreateDTO,
  AppointmentQueryParams,
  AppointmentListDTO,
  TimezoneAbbreviationEnum
} from '../apiTypes.shim';

import type { SetRequired } from 'type-fest';

type CustomerUpcomingAppointmentsParams = Required<Pick<AppointmentQueryParams, 'providerCustomerId' | 'companyId'>>;

type AppointmentAvailabilityParams = Omit<
  SetRequired<Partial<AppointmentQueryParams>, 'callCenterId' | 'companyId' | 'windowDate'>,
  'serializer'
>;

type CustomerUpcomingAppointmentsOptions = UnpagedListOptions<
  CustomerUpcomingAppointmentsParams,
  { requireParams: true }
> & {
  timezone: TimezoneAbbreviationEnum;
};

interface AppointmentResource extends CreateResource<AppointmentCreateDTO, AppointmentDetailDTO> {
  customerUpcomingAppointments: (options: CustomerUpcomingAppointmentsOptions) => Promise<AppointmentListDTO[]>;
  customerUpcomingAppointmentsQueryKey: (
    options: Pick<CustomerUpcomingAppointmentsOptions, 'params' | 'timezone'>
  ) => QueryKey;
  useCustomerUpcomingAppointmentsCached: (
    options: CustomerUpcomingAppointmentsOptions
  ) => CustomQueryHookResult<AppointmentListDTO[]>;
  getAvailabilityAppointments: UnpagedListResource<
    AppointmentAvailabilityDTO,
    AppointmentAvailabilityParams,
    { requireParams: true }
  >['unpagedList'];
  hardDelete: DeleteResource['deleteItem'];
}

export const createAppointmentResource = (): AppointmentResource => {
  const appointmentResourceUri = `appointment`;

  const { create, useCreate, unpagedList } = createApiResource(API, appointmentResourceUri);

  const upcomingDateParams = (timezone: TimezoneAbbreviationEnum): Pick<AppointmentQueryParams, 'date_gte'> => ({
    date_gte: DateUtils.toStartOf(new Date(), 'day', { timezone }).toISOString()
  });

  const customerUpcomingAppointments = async (options: CustomerUpcomingAppointmentsOptions) => {
    const { timezone, ...restOptions } = options;

    const res = await unpagedList<AppointmentListDTO>({
      ...restOptions,
      params: { ...restOptions.params, ...upcomingDateParams(timezone) }
    });

    return res.results;
  };

  const customerUpcomingAppointmentsQueryKey = (
    options: Pick<CustomerUpcomingAppointmentsOptions, 'params' | 'timezone'>
  ) => [
    appointmentResourceUri,
    'UNPAGED_LIST',
    { params: { ...options.params, ...upcomingDateParams(options.timezone) } }
  ];

  const useCustomerUpcomingAppointmentsCached = (options: CustomerUpcomingAppointmentsOptions) =>
    useCustomQuery({
      queryKey: customerUpcomingAppointmentsQueryKey(options),
      queryFn: () => customerUpcomingAppointments(options),
      refetchOnMount: false
    });

  return {
    create,
    useCreate,
    customerUpcomingAppointments,
    customerUpcomingAppointmentsQueryKey,
    useCustomerUpcomingAppointmentsCached,
    getAvailabilityAppointments: (options) =>
      unpagedList<AppointmentAvailabilityDTO>({
        ...options,
        params: { ...options.params, serializer: 'availability' }
      }),
    hardDelete: (id, options) => API.delete(`${appointmentResourceUri}/${id}/hard`, options)
  };
};
